import { useEffect, useMemo, useState } from "react";
import {
  CARRIER_CHANGE_REQUEST_SECTIONS,
  CARRIER_CHANGE_REQUEST_STATUS_LABELS,
  CARRIER_CHANGE_REQUEST_TYPE_LABELS,
  buildTransamericaHierarchyChangeBody,
  buildTransamericaHierarchyChangeSubject,
  type CarrierChangeRequestStatus,
  type CarrierChangeRequestType,
  isFieldRequired,
  isFieldVisible,
} from "@aam/shared";
import { getSessionToken } from "../hooks/useSession";
import { InlineFormField } from "./InlineFormField";
import type { AgentDetailData, ProviderContract } from "./agent-detail/types";
import { Button } from "./ui/Button";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface CarrierChangeRequestRow {
  id: string;
  requestType: CarrierChangeRequestType;
  status: CarrierChangeRequestStatus;
  createdAt: string;
  sentAt: string | null;
  receiptReceivedAt: string | null;
  followUpDueAt: string | null;
  emailSubject: string | null;
  emailBody: string | null;
  notes: string | null;
  formData: Record<string, unknown>;
}

interface CarrierChangeRequestsPanelProps {
  agentId: string;
  user: AgentDetailData;
  contracts: ProviderContract[];
  /** Staff/provider actions: drafts, mark receipt, complete, cancel */
  canManage?: boolean;
  /** Agent or staff can create and send hierarchy change emails */
  canSubmit?: boolean;
}

function formatWhen(value: string | null) {
  return value ? new Date(value).toLocaleString() : "—";
}

export function CarrierChangeRequestsPanel({
  agentId,
  user,
  contracts,
  canManage = false,
  canSubmit,
}: CarrierChangeRequestsPanelProps) {
  const allowSubmit = canSubmit ?? canManage;
  const token = getSessionToken();
  const transamericaContract = contracts.find((contract) => contract.carrier === "transamerica");
  const [requests, setRequests] = useState<CarrierChangeRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [requestData, setRequestData] = useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const defaultFormData = useMemo(() => {
    const agentName = user.displayName || [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
    return {
      agentName,
      agentWritingNumber: transamericaContract?.writingNumber ?? "",
      currentUplineName: user.upline?.displayName ?? "",
      currentUplineWritingNumber: "",
      newUplineName: "",
      newUplineWritingNumber: "",
      reason: "",
    };
  }, [transamericaContract?.writingNumber, user]);

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, token]);

  async function loadRequests() {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/carrier-change-requests?agentId=${agentId}&carrier=transamerica`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to load change requests");
      setRequests(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load change requests");
    } finally {
      setLoading(false);
    }
  }

  function openForm() {
    setRequestData(defaultFormData);
    setShowForm(true);
    setError(null);
  }

  function setFieldValue(fieldId: string, value: unknown) {
    setRequestData((current) => ({ ...current, [fieldId]: value }));
  }

  const previewSubject = useMemo(() => {
    if (!requestData.agentName || !requestData.agentWritingNumber) return "";
    return buildTransamericaHierarchyChangeSubject({
      agentName: String(requestData.agentName),
      agentWritingNumber: String(requestData.agentWritingNumber),
      currentUplineName: String(requestData.currentUplineName ?? ""),
      currentUplineWritingNumber: String(requestData.currentUplineWritingNumber ?? ""),
      newUplineName: String(requestData.newUplineName ?? ""),
      newUplineWritingNumber: String(requestData.newUplineWritingNumber ?? ""),
    });
  }, [requestData]);

  const previewBody = useMemo(() => {
    if (!requestData.agentName || !requestData.agentWritingNumber) return "";
    return buildTransamericaHierarchyChangeBody({
      agentName: String(requestData.agentName),
      agentWritingNumber: String(requestData.agentWritingNumber),
      currentUplineName: String(requestData.currentUplineName ?? ""),
      currentUplineWritingNumber: String(requestData.currentUplineWritingNumber ?? ""),
      newUplineName: String(requestData.newUplineName ?? ""),
      newUplineWritingNumber: String(requestData.newUplineWritingNumber ?? ""),
    });
  }, [requestData]);

  async function createRequest(submitNow: boolean) {
    if (!token) return;
    const section = CARRIER_CHANGE_REQUEST_SECTIONS.HIERARCHY_CHANGE;
    const errors: string[] = [];
    for (const field of section.fields) {
      if (!isFieldVisible(field, requestData)) continue;
      if (!isFieldRequired(field, requestData)) continue;
      const value = requestData[field.id];
      if (value === undefined || value === null || value === "") errors.push(field.label);
    }
    if (errors.length > 0) {
      setError(`Missing: ${errors.join(", ")}`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = Object.fromEntries(
        section.fields
          .filter((field) => isFieldVisible(field, requestData))
          .map((field) => [field.id, requestData[field.id]])
      );
      const res = await fetch(`${API_BASE}/api/carrier-change-requests`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "HIERARCHY_CHANGE",
          agentUserId: agentId,
          formData: payload,
          submitNow,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to create request");
      setShowForm(false);
      setRequestData({});
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create request");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitDraft(requestId: string) {
    if (!token) return;
    setActionId(requestId);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/carrier-change-requests/${requestId}/submit`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to submit request");
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit request");
    } finally {
      setActionId(null);
    }
  }

  async function patchRequest(
    requestId: string,
    body: Record<string, unknown>
  ) {
    if (!token) return;
    setActionId(requestId);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/carrier-change-requests/${requestId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to update request");
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update request");
    } finally {
      setActionId(null);
    }
  }

  if (!transamericaContract) {
    return null;
  }

  return (
    <section className="space-y-4 rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-content-primary">Transamerica change requests</h2>
          <p className="mt-1 text-sm text-content-secondary">
            Email hierarchy changes to Transamerica contract administration and track receipt and follow-up.
          </p>
        </div>
        {allowSubmit && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={openForm}
          >
            New hierarchy change
          </Button>
        )}
      </div>

      {showForm && (
        <div className="space-y-4 rounded-lg border border-border-default bg-surface-overlay p-4">
          <h3 className="text-sm font-semibold text-content-primary">
            {CARRIER_CHANGE_REQUEST_SECTIONS.HIERARCHY_CHANGE.title}
          </h3>
          {CARRIER_CHANGE_REQUEST_SECTIONS.HIERARCHY_CHANGE.fields.map((field) => (
            <InlineFormField
              key={field.id}
              field={{
                ...field,
                required: isFieldRequired(field, requestData),
              }}
              value={requestData[field.id]}
              onChange={(nextValue) => setFieldValue(field.id, nextValue)}
            />
          ))}

          {(previewSubject || previewBody) && (
            <div className="space-y-2 rounded-lg border border-border-subtle bg-surface-base p-3">
              <p className="text-xs uppercase tracking-wide text-content-muted">Email preview</p>
              {previewSubject && (
                <p className="text-sm text-content-primary">
                  <span className="text-content-muted">Subject:</span> {previewSubject}
                </p>
              )}
              {previewBody && (
                <pre className="whitespace-pre-wrap text-xs text-content-secondary">{previewBody}</pre>
              )}
            </div>
          )}

          {error && <p role="alert" className="text-sm text-status-rejected-text">{error}</p>}
          <div className="flex flex-wrap gap-2 [&>*]:w-full sm:[&>*]:w-auto">
            <Button
              type="button"
              disabled={submitting}
              onClick={() => createRequest(true)}
            >
              {submitting ? "Sending..." : "Send to Transamerica"}
            </Button>
            {canManage && (
              <Button
                type="button"
                disabled={submitting}
                onClick={() => createRequest(false)}
                variant="secondary"
              >
                Save draft
              </Button>
            )}
            <Button
              type="button"
              onClick={() => setShowForm(false)}
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {error && !showForm && <p role="alert" className="text-sm text-status-rejected-text">{error}</p>}

      {loading ? (
        <div role="status" aria-busy="true" className="space-y-3" aria-label="Loading change requests">
          <div className="h-24 animate-pulse rounded-lg bg-skeleton" />
          <div className="h-24 animate-pulse rounded-lg bg-skeleton" />
        </div>
      ) : requests.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border-default p-6 text-center text-sm text-content-muted">No Transamerica change requests yet.</p>
      ) : (
        <div className="space-y-3">
          {requests.map((request) => (
            <article key={request.id} className="space-y-2 rounded-lg border border-border-subtle bg-surface-overlay p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-content-primary">
                  {CARRIER_CHANGE_REQUEST_TYPE_LABELS[request.requestType]}
                </span>
                <span className="text-xs font-semibold uppercase text-content-secondary">
                  {CARRIER_CHANGE_REQUEST_STATUS_LABELS[request.status]}
                </span>
              </div>
              <p className="text-xs text-content-muted">Created {formatWhen(request.createdAt)}</p>
              {request.sentAt && (
                <p className="text-xs text-content-secondary">Sent to Transamerica {formatWhen(request.sentAt)}</p>
              )}
              {request.receiptReceivedAt && (
                <p className="text-xs text-content-secondary">Receipt confirmed {formatWhen(request.receiptReceivedAt)}</p>
              )}
              {request.followUpDueAt && (
                <p className="text-xs text-status-pending-text">Follow-up due {formatWhen(request.followUpDueAt)}</p>
              )}
              {request.emailSubject && (
                <p className="text-xs text-content-secondary">Subject: {request.emailSubject}</p>
              )}

              {canManage && request.status !== "COMPLETED" && request.status !== "CANCELLED" && (
                <div className="flex flex-wrap gap-2 pt-1 [&>*]:min-h-11 [&>*]:w-full sm:[&>*]:w-auto">
                  {request.status === "DRAFT" && (
                    <button
                      type="button"
                      disabled={actionId === request.id}
                      onClick={() => submitDraft(request.id)}
                      className="rounded-lg bg-accent-strong px-3 py-2 text-xs text-on-accent"
                    >
                      Send to Transamerica
                    </button>
                  )}
                  {request.status === "SUBMITTED" && (
                    <button
                      type="button"
                      disabled={actionId === request.id}
                      onClick={() => patchRequest(request.id, { markReceiptReceived: true })}
                      className="rounded-lg border border-border-default px-3 py-2 text-xs text-content-primary"
                    >
                      Mark receipt received
                    </button>
                  )}
                  {(request.status === "EMAIL_RECEIVED" || request.status === "FOLLOW_UP_DUE") && (
                    <button
                      type="button"
                      disabled={actionId === request.id}
                      onClick={() => patchRequest(request.id, { status: "COMPLETED" })}
                      className="rounded-lg bg-status-active-bg border border-status-active-border px-3 py-2 text-xs text-status-active-text"
                    >
                      Mark completed
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={actionId === request.id}
                    onClick={() => patchRequest(request.id, { status: "CANCELLED" })}
                    className="rounded-lg border border-status-rejected-border px-3 py-2 text-xs text-status-rejected-text"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
